import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { FaFileExcel, FaFilePdf, FaRoute, FaCar, FaBox } from "react-icons/fa";
import { IoWarningOutline } from "react-icons/io5";
import { getToken } from "../../api/auth";
import { calculateRouteOptimization } from "../../api/routeOptimization";
import { getSalesHeaders } from "../../api/order";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./RouteOptimization.css";

const RouteOptimization = () => {
  const token = getToken();
  const [matchedOrders, setMatchedOrders] = useState([]);
  const [nonOptimizedOrders, setNonOptimizedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nonOptLoading, setNonOptLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPageNonOpt, setCurrentPageNonOpt] = useState(1);
  const [groupPages, setGroupPages] = useState({});
  const PAGE_SIZE = 100;
  const [ordersPage, setOrdersPage] = useState(1);
  const [hasMoreOrders, setHasMoreOrders] = useState(true);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const itemsPerPage = 5;
  const navigate = useNavigate();
  const [calculationSuccess, setCalculationSuccess] = useState(false);

  const loadNonOptimizedOrders = async (page = 1, append = false) => {
    if (page === 1) setNonOptLoading(true);
    else setLoadMoreLoading(true);

    try {
      const salesResponse = await getSalesHeaders(page, PAGE_SIZE, "Finished");

      if (salesResponse.success && salesResponse.orders) {
        const totalReceived = salesResponse.orders.length;
        const filtered = salesResponse.orders.filter(
          (order) => order.optimized === 0,
        );

        setNonOptimizedOrders((prev) =>
          append ? [...prev, ...filtered] : filtered,
        );
        setOrdersPage(page);

        // hasMoreOrders based on backend response, not filtered results
        setHasMoreOrders(totalReceived === PAGE_SIZE);
      } else {
        setHasMoreOrders(false);
      }
    } catch (err) {
      console.error("Failed to load non-optimized orders:", err);
      setError(`Failed to load non-optimized orders: ${err.message}`);
    } finally {
      if (page === 1) setNonOptLoading(false);
      else setLoadMoreLoading(false);
    }
  };

  useEffect(() => {
    loadNonOptimizedOrders(1, false);
  }, []);

  const handleLoadMoreNonOptimized = () => {
    if (loadMoreLoading || !hasMoreOrders) return;
    loadNonOptimizedOrders(ordersPage + 1, true);
  };

  const handleCalculateRoutes = async (e) => {
    e.preventDefault();
    console.log("handleCalculateRoutes triggered with:", {
      fromDate,
      toDate,
      token,
    });
    if (!fromDate || !toDate) {
      setError("Please select both start and end dates.");
      setCalculationSuccess(false);
      return;
    }
    setLoading(true);
    setError(null);
    setCalculationSuccess(false);
    setMatchedOrders([]); // Clear previous results
    try {
      // Call route optimization API
      const routeResponse = await calculateRouteOptimization(fromDate, toDate);

      if (
        !routeResponse.success ||
        !routeResponse.routeData ||
        !routeResponse.routeData.routes
      ) {
        setError(
          routeResponse.message ||
            "No valid data available for the selected date range."
        );
        return;
      }

      // Success - show button to navigate to results
      setCalculationSuccess(true);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to calculate routes";
      setError(errorMessage);
      setCalculationSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = (orders, vehicleID) => {
    if (!orders.length) return;

    const data = orders.map((order) => ({
      VehicleID: order.vehicleID,
      OrderNo: order.no,
      CustomerNo: order.sellToCustomerNo,
      CustomerName: order.sellToCustomerName,
      Address: order.sellToAddress,
      City: order.sellToCity,
      OrderDate: order.orderDate,
      ExternalDocumentNo: order.externalDocumentNo,
      HeaderAmount: Number(order.headerAmount).toFixed(2),
      PaymentMethod: order.paymentMethodCode,
      SalesLinesCount: order.salesLinesCount,
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    // Define modern styles with light blue header
    const headerStyle = {
      font: { name: "Arial", sz: 12, bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "81D4FA" } }, // Light blue header
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "medium", color: { rgb: "333333" } },
        bottom: { style: "medium", color: { rgb: "333333" } },
        left: { style: "medium", color: { rgb: "333333" } },
        right: { style: "medium", color: { rgb: "333333" } },
      },
    };

    const rowStyleEven = {
      font: { name: "Arial", sz: 11, color: { rgb: "333333" } },
      fill: { patternType: "solid", fgColor: { rgb: "E6F3F3" } },
      border: {
        top: { style: "thin", color: { rgb: "999999" } },
        bottom: { style: "thin", color: { rgb: "999999" } },
        left: { style: "thin", color: { rgb: "999999" } },
        right: { style: "thin", color: { rgb: "999999" } },
      },
    };

    const rowStyleOdd = {
      font: { name: "Arial", sz: 11, color: { rgb: "333333" } },
      fill: { patternType: "solid", fgColor: { rgb: "F0F8F8" } },
      border: {
        top: { style: "thin", color: { rgb: "999999" } },
        bottom: { style: "thin", color: { rgb: "999999" } },
        left: { style: "thin", color: { rgb: "999999" } },
        right: { style: "thin", color: { rgb: "999999" } },
      },
    };

    // Apply styles to headers
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      worksheet[cellAddress].s = headerStyle;
    }

    // Apply alternating row styles
    for (let R = 1; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        worksheet[cellAddress].s = R % 2 === 0 ? rowStyleEven : rowStyleOdd;
      }
    }

    // Format HeaderAmount column as currency
    const headerAmountColIndex = data[0]
      ? Object.keys(data[0]).indexOf("HeaderAmount")
      : -1;
    if (headerAmountColIndex !== -1) {
      for (let R = 1; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({
          r: R,
          c: headerAmountColIndex,
        });
        worksheet[cellAddress].z = '"€"#,##0.00';
      }
    }

    // Auto-adjust column widths with more generous spacing
    const colWidths = Object.keys(data[0]).map((key, i) => {
      const columnValues = data.map((row) => String(row[key]));
      const maxLength = Math.max(
        key.length,
        ...columnValues.map((val) => val.length),
      );
      return { wch: Math.min(maxLength + 4, 35) }; // Increased padding, max width 35
    });
    worksheet["!cols"] = colWidths;

    // Set row heights
    worksheet["!rows"] = [{ hpt: 25 }]; // Header row height
    for (let i = 1; i <= range.e.r; i++) {
      worksheet["!rows"][i] = { hpt: 20 }; // Data row height
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, "Optimized Orders");
    XLSX.writeFile(
      workbook,
      `OptimizedOrders_${vehicleID}_${fromDate}_to_${toDate}.xlsx`,
    );
  };

  const exportToPDF = (orders, vehicleID) => {
    if (!orders.length) return;

    try {
      const doc = new jsPDF({ unit: "mm" });

      // Title
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(
        `Optimized Orders for ${vehicleID} from ${fromDate} to ${toDate}`,
        14,
        20,
      );

      // Prepare table data
      const tableColumn = [
        "Vehicle ID",
        "Order No",
        "Customer No",
        "Customer Name",
        "Address",
        "City",
        "Order Date",
        "Header Amount (€)",
        "Payment Method",
        "Sales Lines",
      ];
      const tableRows = orders.map((order) => [
        order.vehicleID,
        order.no,
        order.sellToCustomerNo,
        order.sellToCustomerName,
        order.sellToAddress,
        order.sellToCity,
        order.orderDate,
        Number(order.headerAmount).toFixed(2),
        order.paymentMethodCode,
        order.salesLinesCount,
      ]);

      // Generate table using autoTable
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 30,
        styles: {
          fontSize: 8,
          cellPadding: 3,
          overflow: "linebreak",
          halign: "left",
          valign: "middle",
        },
        headStyles: {
          fillColor: [129, 212, 250], // Light blue background
          textColor: [0, 0, 0],
          fontStyle: "bold",
          halign: "center",
        },
        alternateRowStyles: {
          fillColor: [246, 248, 250], // Light gray for alternating rows
        },
        columnStyles: {
          0: { cellWidth: 16 }, // Vehicle ID
          1: { cellWidth: 18 }, // Order No
          2: { cellWidth: 19 }, // Customer No
          3: { cellWidth: 30 }, // Customer Name
          4: { cellWidth: 30 }, // Address
          5: { cellWidth: 18 }, // City
          6: { cellWidth: 17 }, // Order Date
          7: { cellWidth: 17 }, // Header Amount
          8: { cellWidth: 16 }, // Payment Method
          9: { cellWidth: 15 }, // Sales Lines
        },
        margin: { top: 30, left: 10, right: 10, bottom: 10 },
        theme: "grid",
        didDrawPage: function (data) {
          // Add page numbers
          let str = doc.splitTextToSize(`Page ${data.pageNumber}`, 100);
          doc.text(
            str,
            data.settings.margin.left,
            doc.internal.pageSize.height - 10,
          );
        },
      });

      doc.save(`OptimizedOrders_${vehicleID}_${fromDate}_to_${toDate}.pdf`);
    } catch (err) {
      console.error("Failed to generate PDF:", err);
      setError(`Failed to generate PDF: ${err.message}`);
    }
  };

  if (!token) {
    console.log("No token, redirecting to login");
    return <Navigate to="/login" replace />;
  }

  // Pagination logic for non-optimized orders
  const indexOfLastNonOpt = currentPageNonOpt * itemsPerPage;
  const indexOfFirstNonOpt = indexOfLastNonOpt - itemsPerPage;
  const currentNonOptOrders = nonOptimizedOrders.slice(
    indexOfFirstNonOpt,
    indexOfLastNonOpt,
  );
  const totalPagesNonOpt = Math.ceil(nonOptimizedOrders.length / itemsPerPage);

  const handlePrevPageNonOpt = () => {
    setCurrentPageNonOpt((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPageNonOpt = () => {
    setCurrentPageNonOpt((prev) => Math.min(prev + 1, totalPagesNonOpt));
  };

  // Group matchedOrders by vehicleID
  const groupedOrders = matchedOrders.reduce((acc, order) => {
    const vid = order.vehicleID;
    if (!acc[vid]) {
      acc[vid] = [];
    }
    acc[vid].push(order);
    return acc;
  }, {});

  return (
    <div className="route-optimization-container">
      {/* Header Section */}
      <div className="ro-header">
        <div className="ro-header-content">
          <div className="ro-title-section">
            <FaRoute className="ro-title-icon" size={28} />
            <div>
              <h1 className="ro-main-title">Route Optimization</h1>
              <p className="ro-subtitle">
                Plan and optimize delivery routes efficiently
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="ro-error-banner">
          <span className="ro-error-icon">
            <IoWarningOutline />
          </span>
          <span>{error}</span>
        </div>
      )}

      {/* Filters Card */}
      <div className="ro-card ro-filters-card">
        <div className="ro-card-header">
          <h2 className="ro-card-title">Route Calculation</h2>
          <p className="ro-card-subtitle">
            Select date range to calculate optimized routes
          </p>
        </div>
        <form className="ro-filters-form" onSubmit={handleCalculateRoutes}>
          <div className="ro-input-group">
            <label className="ro-label">From Date</label>
            <input
              type="date"
              className="ro-input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              required
            />
          </div>
          <div className="ro-input-group">
            <label className="ro-label">To Date</label>
            <input
              type="date"
              className="ro-input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={fromDate}
              required
            />
          </div>
          <button
            type="submit"
            className="ro-btn ro-btn-primary"
            disabled={loading}
          >
            <FaRoute size={16} />
            <span>{loading ? "Calculating..." : "Calculate Routes"}</span>
          </button>
        </form>
      </div>

      {/* Non-Optimized Orders Section */}
      <div className="ro-card">
        <div className="ro-card-header ro-header-with-action">
          <div>
            <h2 className="ro-card-title">Non-Optimized Orders</h2>
            <p className="ro-card-subtitle">
              {nonOptimizedOrders.length} order
              {nonOptimizedOrders.length !== 1 ? "s" : ""} pending optimization
            </p>
          </div>
          <button
            className="ro-btn ro-btn-secondary ro-btn-sm"
            onClick={handleLoadMoreNonOptimized}
            disabled={loadMoreLoading || !hasMoreOrders}
          >
            {loadMoreLoading ? "Loading..." : "+ Load 100 More"}
          </button>
        </div>

        {nonOptLoading ? (
          <div className="ro-loading">
            <div className="ro-spinner"></div>
            <p className="ro-loading-text">Loading non-optimized orders...</p>
          </div>
        ) : nonOptimizedOrders.length > 0 ? (
          <>
            <div className="ro-table-wrapper">
              <table className="ro-table">
                <thead>
                  <tr>
                    {[
                      { key: "vehicleID", label: "Vehicle" },
                      { key: "no", label: "Order No" },
                      { key: "sellToCustomerNo", label: "Customer No" },
                      { key: "sellToCustomerName", label: "Customer Name" },
                      { key: "sellToAddress", label: "Address" },
                      { key: "sellToCity", label: "City" },
                      { key: "orderDate", label: "Date" },
                      { key: "externalDocumentNo", label: "External Doc" },
                      { key: "headerAmount", label: "Amount (€)" },
                      { key: "paymentMethodCode", label: "Payment" },
                      { key: "salesLinesCount", label: "Lines" },
                    ].map(({ key, label }) => (
                      <th key={key}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentNonOptOrders.map((order, idx) => (
                    <tr key={order.no} className="ro-table-row">
                      <td>
                        <span className="ro-badge ro-badge-gray">N/A</span>
                      </td>
                      <td className="ro-cell-primary">{order.no}</td>
                      <td>{order.sellToCustomerNo}</td>
                      <td className="ro-cell-name">
                        {order.sellToCustomerName}
                      </td>
                      <td className="ro-cell-secondary">
                        {order.sellToAddress}
                      </td>
                      <td>{order.sellToCity}</td>
                      <td>{order.orderDate}</td>
                      <td>{order.externalDocumentNo}</td>
                      <td className="ro-cell-amount">
                        €{Number(order.headerAmount).toFixed(2)}
                      </td>
                      <td>{order.paymentMethodCode}</td>
                      <td>
                        <span className="ro-badge ro-badge-blue">
                          {order.saleslines ? order.saleslines.length : 0}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards View */}
            <div className="ro-cards-grid">
              {currentNonOptOrders.map((order, idx) => (
                <div key={order.no} className="ro-order-card">
                  <div className="ro-order-card-header">
                    <span className="ro-order-number">{order.no}</span>
                    <span className="ro-badge ro-badge-gray">
                      Not Optimized
                    </span>
                  </div>
                  <div className="ro-order-card-body">
                    <div className="ro-order-field">
                      <span className="ro-field-label">Customer:</span>
                      <span className="ro-field-value">
                        {order.sellToCustomerName}
                      </span>
                    </div>
                    <div className="ro-order-field">
                      <span className="ro-field-label">Customer No:</span>
                      <span className="ro-field-value">
                        {order.sellToCustomerNo}
                      </span>
                    </div>
                    <div className="ro-order-field">
                      <span className="ro-field-label">Address:</span>
                      <span className="ro-field-value">
                        {order.sellToAddress}
                      </span>
                    </div>
                    <div className="ro-order-field">
                      <span className="ro-field-label">City:</span>
                      <span className="ro-field-value">{order.sellToCity}</span>
                    </div>
                    <div className="ro-order-field">
                      <span className="ro-field-label">Date:</span>
                      <span className="ro-field-value">{order.orderDate}</span>
                    </div>
                    <div className="ro-order-field">
                      <span className="ro-field-label">External Doc:</span>
                      <span className="ro-field-value">
                        {order.externalDocumentNo}
                      </span>
                    </div>
                    <div className="ro-order-field">
                      <span className="ro-field-label">Amount:</span>
                      <span className="ro-field-value ro-amount">
                        €{Number(order.headerAmount).toFixed(2)}
                      </span>
                    </div>
                    <div className="ro-order-field">
                      <span className="ro-field-label">Payment:</span>
                      <span className="ro-field-value">
                        {order.paymentMethodCode}
                      </span>
                    </div>
                    <div className="ro-order-field">
                      <span className="ro-field-label">Sales Lines:</span>
                      <span className="ro-badge ro-badge-blue">
                        {order.saleslines ? order.saleslines.length : 0}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="ro-pagination">
              <button
                className="ro-btn ro-btn-outline ro-btn-sm"
                onClick={handlePrevPageNonOpt}
                disabled={currentPageNonOpt === 1}
              >
                Previous
              </button>
              <span className="ro-pagination-info">
                Page {currentPageNonOpt} of {totalPagesNonOpt}
              </span>
              <button
                className="ro-btn ro-btn-outline ro-btn-sm"
                onClick={handleNextPageNonOpt}
                disabled={currentPageNonOpt === totalPagesNonOpt}
              >
                Next
              </button>
            </div>
          </>
        ) : (
          <div className="ro-empty-state">
            <div className="ro-empty-icon">
              <FaBox></FaBox>
            </div>
            <p className="ro-empty-title">No Non-Optimized Orders</p>
            <p className="ro-empty-text">All orders have been optimized</p>
          </div>
        )}
      </div>

      {/* Optimized Orders by Vehicle */}
      {loading ? (
        <div className="ro-card">
          <div className="ro-loading">
            <div className="ro-spinner"></div>
            <p className="ro-loading-text">Calculating optimized routes...</p>
          </div>
        </div>
      ) : calculationSuccess ? (
        <div className="ro-card">
          <div className="ro-success-state">
            <div className="ro-success-icon">
              <FaRoute size={48} />
            </div>
            <h2 className="ro-success-title">Route Calculation Complete!</h2>
            <p className="ro-success-message">
              Your routes have been optimized successfully. Click the button
              below to view detailed route results.
            </p>
            <button
              className="ro-btn ro-btn-primary ro-btn-lg"
              onClick={() =>
                navigate("/route-results", { state: { fromDate, toDate } })
              }
            >
              <FaRoute size={18} />
              <span>View Route Results</span>
            </button>
          </div>
        </div>
      ) : matchedOrders.length > 0 ? (
        <div className="ro-vehicles-section">
          {Object.keys(groupedOrders).map((vehicleID) => {
            const groupOrders = groupedOrders[vehicleID];
            const currentPage = groupPages[vehicleID] || 1;
            const indexOfLast = currentPage * itemsPerPage;
            const indexOfFirst = indexOfLast - itemsPerPage;
            const currentGroupOrders = groupOrders.slice(
              indexOfFirst,
              indexOfLast,
            );
            const totalPagesGroup = Math.ceil(
              groupOrders.length / itemsPerPage,
            );

            const handlePrevPageGroup = () => {
              setGroupPages((prev) => ({
                ...prev,
                [vehicleID]: Math.max((prev[vehicleID] || 1) - 1, 1),
              }));
            };

            const handleNextPageGroup = () => {
              setGroupPages((prev) => ({
                ...prev,
                [vehicleID]: Math.min(
                  (prev[vehicleID] || 1) + 1,
                  totalPagesGroup,
                ),
              }));
            };

            return (
              <div key={vehicleID} className="ro-card ro-vehicle-card">
                <div className="ro-card-header ro-header-with-action">
                  <div className="ro-vehicle-header">
                    <FaCar size={20} className="ro-vehicle-icon" />
                    <div>
                      <h3 className="ro-card-title">Vehicle: {vehicleID}</h3>
                      <p className="ro-card-subtitle">
                        {groupOrders.length} optimized orders
                      </p>
                    </div>
                  </div>
                  <div className="ro-export-buttons">
                    <button
                      className="ro-btn ro-btn-success ro-btn-sm"
                      onClick={() => exportToExcel(groupOrders, vehicleID)}
                    >
                      <FaFileExcel size={14} />
                      <span>Excel</span>
                    </button>
                    <button
                      className="ro-btn ro-btn-danger ro-btn-sm"
                      onClick={() => exportToPDF(groupOrders, vehicleID)}
                    >
                      <FaFilePdf size={14} />
                      <span>PDF</span>
                    </button>
                  </div>
                </div>

                <div className="ro-table-wrapper">
                  <table className="ro-table">
                    <thead>
                      <tr>
                        {[
                          { key: "vehicleID", label: "Vehicle" },
                          { key: "no", label: "Order No" },
                          { key: "sellToCustomerNo", label: "Customer No" },
                          { key: "sellToCustomerName", label: "Customer Name" },
                          { key: "sellToAddress", label: "Address" },
                          { key: "sellToCity", label: "City" },
                          { key: "orderDate", label: "Date" },
                          { key: "externalDocumentNo", label: "External Doc" },
                          { key: "headerAmount", label: "Amount (€)" },
                          { key: "paymentMethodCode", label: "Payment" },
                          { key: "salesLinesCount", label: "Lines" },
                        ].map(({ key, label }) => (
                          <th key={key}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentGroupOrders.map((order, idx) => (
                        <tr key={order.no} className="ro-table-row">
                          <td>
                            <span className="ro-badge ro-badge-green">
                              {order.vehicleID}
                            </span>
                          </td>
                          <td className="ro-cell-primary">{order.no}</td>
                          <td>{order.sellToCustomerNo}</td>
                          <td className="ro-cell-name">
                            {order.sellToCustomerName}
                          </td>
                          <td className="ro-cell-secondary">
                            {order.sellToAddress}
                          </td>
                          <td>{order.sellToCity}</td>
                          <td>{order.orderDate}</td>
                          <td>{order.externalDocumentNo}</td>
                          <td className="ro-cell-amount">
                            €{Number(order.headerAmount).toFixed(2)}
                          </td>
                          <td>{order.paymentMethodCode}</td>
                          <td>
                            <span className="ro-badge ro-badge-blue">
                              {order.salesLinesCount}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards View */}
                <div className="ro-cards-grid">
                  {currentGroupOrders.map((order, idx) => (
                    <div key={order.no} className="ro-order-card">
                      <div className="ro-order-card-header">
                        <span className="ro-order-number">{order.no}</span>
                        <span className="ro-badge ro-badge-green">
                          {order.vehicleID}
                        </span>
                      </div>
                      <div className="ro-order-card-body">
                        <div className="ro-order-field">
                          <span className="ro-field-label">Customer:</span>
                          <span className="ro-field-value">
                            {order.sellToCustomerName}
                          </span>
                        </div>
                        <div className="ro-order-field">
                          <span className="ro-field-label">Customer No:</span>
                          <span className="ro-field-value">
                            {order.sellToCustomerNo}
                          </span>
                        </div>
                        <div className="ro-order-field">
                          <span className="ro-field-label">Address:</span>
                          <span className="ro-field-value">
                            {order.sellToAddress}
                          </span>
                        </div>
                        <div className="ro-order-field">
                          <span className="ro-field-label">City:</span>
                          <span className="ro-field-value">
                            {order.sellToCity}
                          </span>
                        </div>
                        <div className="ro-order-field">
                          <span className="ro-field-label">Date:</span>
                          <span className="ro-field-value">
                            {order.orderDate}
                          </span>
                        </div>
                        <div className="ro-order-field">
                          <span className="ro-field-label">External Doc:</span>
                          <span className="ro-field-value">
                            {order.externalDocumentNo}
                          </span>
                        </div>
                        <div className="ro-order-field">
                          <span className="ro-field-label">Amount:</span>
                          <span className="ro-field-value ro-amount">
                            €{Number(order.headerAmount).toFixed(2)}
                          </span>
                        </div>
                        <div className="ro-order-field">
                          <span className="ro-field-label">Payment:</span>
                          <span className="ro-field-value">
                            {order.paymentMethodCode}
                          </span>
                        </div>
                        <div className="ro-order-field">
                          <span className="ro-field-label">Sales Lines:</span>
                          <span className="ro-badge ro-badge-blue">
                            {order.salesLinesCount}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                <div className="ro-pagination">
                  <button
                    className="ro-btn ro-btn-outline ro-btn-sm"
                    onClick={handlePrevPageGroup}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span className="ro-pagination-info">
                    Page {currentPage} of {totalPagesGroup}
                  </span>
                  <button
                    className="ro-btn ro-btn-outline ro-btn-sm"
                    onClick={handleNextPageGroup}
                    disabled={currentPage === totalPagesGroup}
                  >
                    Next
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        !loading && (
          <div className="ro-card">
            <div className="ro-empty-state">
              <div className="ro-empty-icon">
                <FaCar></FaCar>
              </div>
              <p className="ro-empty-title">No Optimized Routes</p>
              <p className="ro-empty-text">
                Calculate routes to see optimized delivery orders
              </p>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default RouteOptimization;
